import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from '@phosphor-icons/react';
import { IconButton } from './IconButton';
import styles from './ui.module.css';

/** Elements that can receive focus, for the trap boundary. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /** Set false for dialogs that must be dismissed deliberately, e.g. before watching. */
  dismissible?: boolean;
  labelledBy?: string;
  /**
   * Replaces the default title/description block with arbitrary content.
   *
   * `title` still labels the dialog for assistive technology; this only changes what sighted
   * users see. It exists so a dialog with its own identity - the account popup's brand lockup -
   * does not have to fight the generic header or abandon the sheet, focus trap and dismissal the
   * `Modal` provides.
   */
  header?: ReactNode;
}

/**
 * Bottom sheet on phones, centred panel on tablet up. Both share the behaviour:
 * escape closes it, focus moves in on open and returns to the trigger on close,
 * and the page behind stops scrolling.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  dismissible = true,
  header,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        event.stopPropagation();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (element) => element.offsetParent !== null,
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [dismissible, onClose],
  );

  useEffect(() => {
    if (!open) return;

    restoreFocusRef.current = document.activeElement as HTMLElement | null;

    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown, true);

    // Move focus into the panel once it is in the DOM.
    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target = panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
      target.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', handleKeyDown, true);
      restoreFocusRef.current?.focus();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return createPortal(
    <div
      className="overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <div className={styles.modalHeader ?? ''}>
          {header ?? (
            <div>
              <h2 className={styles.modalTitle ?? ''}>{title}</h2>
              {description ? <p className={styles.modalDescription ?? ''}>{description}</p> : null}
            </div>
          )}
          {dismissible ? (
            <IconButton label="Close" icon={<X size={18} />} onClick={onClose} size="sm" />
          ) : null}
        </div>

        {children}

        {footer ? <div className={styles.modalFooter ?? ''}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}